/**
 * Compliance Declarations Form Component
 * Sixth step of enhanced manufacturer registration
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../ui';
import { ComplianceDeclarations } from '../../services/registration.service';

interface ComplianceDeclarationsFormProps {
  data?: ComplianceDeclarations;
  onSave: (data: ComplianceDeclarations) => Promise<void>;
  onNext: () => void;
  onPrevious: () => void;
  saving: boolean;
}

export function ComplianceDeclarationsForm({ data, onSave, onNext, onPrevious, saving }: ComplianceDeclarationsFormProps) {
  const [formData, setFormData] = useState<ComplianceDeclarations>({
    declarationsAccepted: false,
    termsAccepted: false,
    privacyPolicyAccepted: false,
    dataProcessingConsent: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.declarationsAccepted) {
      newErrors.declarationsAccepted = 'You must accept the legal declarations';
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the Terms of Service';
    }

    if (!formData.privacyPolicyAccepted) {
      newErrors.privacyPolicyAccepted = 'You must accept the Privacy Policy';
    }

    if (!formData.dataProcessingConsent) {
      newErrors.dataProcessingConsent = 'You must provide consent for data processing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSave(formData);
      onNext();
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Legal Declarations */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Legal Declarations</h3>
        
        <div className="space-y-4">
          {/* Information Accuracy */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="declarationsAccepted"
              checked={formData.declarationsAccepted}
              onChange={(e) => setFormData(prev => ({ ...prev, declarationsAccepted: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
            />
            <label htmlFor="declarationsAccepted" className="ml-3 text-sm text-gray-700">
              <span className="font-medium">Information Accuracy:</span> I hereby declare that all information provided in this registration is true, accurate, and complete to the best of my knowledge. I understand that providing false information may result in rejection of my application or termination of services.
            </label>
          </div>
          {errors.declarationsAccepted && (
            <p className="ml-7 text-sm text-red-600">{errors.declarationsAccepted}</p>
          )}

          {/* Brand Ownership */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="brandOwnership"
              checked={formData.declarationsAccepted}
              onChange={(e) => setFormData(prev => ({ ...prev, declarationsAccepted: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
            />
            <label htmlFor="brandOwnership" className="ml-3 text-sm text-gray-700">
              <span className="font-medium">Brand Ownership:</span> I confirm that I own the brands mentioned or have legal rights to sell products under these brand names. I understand that any counterfeit or unauthorized use of brands will result in legal action.
            </label>
          </div>

          {/* QR Code Policy */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="qrCodePolicy"
              checked={formData.declarationsAccepted}
              onChange={(e) => setFormData(prev => ({ ...prev, declarationsAccepted: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
            />
            <label htmlFor="qrCodePolicy" className="ml-3 text-sm text-gray-700">
              <span className="font-medium">QR Code Policy:</span> I agree that QR codes generated through this platform will not be reused, duplicated, or transferred to unauthorized parties. Each QR code is unique and tied to specific products.
            </label>
          </div>

          {/* Liability */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="liability"
              checked={formData.declarationsAccepted} // Using same field for simplicity
              onChange={(e) => setFormData(prev => ({ ...prev, declarationsAccepted: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
            />
            <label htmlFor="liability" className="ml-3 text-sm text-gray-700">
              <span className="font-medium">Liability:</span> I understand that liability for counterfeit products lies with the manufacturer. PureAum provides authentication services but does not guarantee product authenticity beyond the technical verification of QR codes.
            </label>
          </div>
        </div>
      </div>

      {/* Terms and Policies */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Terms and Policies</h3>
        
        <div className="space-y-4">
          {/* Terms of Service */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="termsAccepted"
              checked={formData.termsAccepted}
              onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
            />
            <label htmlFor="termsAccepted" className="ml-3 text-sm text-gray-700">
              I have read and agree to the <a href="/terms" target="_blank" className="text-blue-600 hover:underline">Terms of Service</a> and <a href="/msa" target="_blank" className="text-blue-600 hover:underline">Master Service Agreement</a>.
            </label>
          </div>
          {errors.termsAccepted && (
            <p className="ml-7 text-sm text-red-600">{errors.termsAccepted}</p>
          )}

          {/* Privacy Policy */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="privacyPolicyAccepted"
              checked={formData.privacyPolicyAccepted}
              onChange={(e) => setFormData(prev => ({ ...prev, privacyPolicyAccepted: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
            />
            <label htmlFor="privacyPolicyAccepted" className="ml-3 text-sm text-gray-700">
              I have read and agree to the <a href="/privacy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>.
            </label>
          </div>
          {errors.privacyPolicyAccepted && (
            <p className="ml-7 text-sm text-red-600">{errors.privacyPolicyAccepted}</p>
          )}

          {/* Data Processing Consent */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="dataProcessingConsent"
              checked={formData.dataProcessingConsent}
              onChange={(e) => setFormData(prev => ({ ...prev, dataProcessingConsent: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
            />
            <label htmlFor="dataProcessingConsent" className="ml-3 text-sm text-gray-700">
              <span className="font-medium">Data Processing Consent (DPDP Act 2023):</span> I consent to the processing of my personal and business data as described in the <a href="/data-processing" target="_blank" className="text-blue-600 hover:underline">Data Processing Agreement</a>. I understand my rights regarding data access, correction, and deletion.
            </label>
          </div>
          {errors.dataProcessingConsent && (
            <p className="ml-7 text-sm text-red-600">{errors.dataProcessingConsent}</p>
          )}
        </div>
      </div>

      {/* Important Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Important Notice</h3>
            <div className="text-sm text-yellow-700 mt-1">
              <p>By accepting these declarations, you are entering into a legally binding agreement. Please ensure you have the authority to make these commitments on behalf of your organization.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
        >
          Previous
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="px-6 py-2"
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </div>
    </form>
  );
}
